<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import * as Sentry from '@sentry/nuxt'
import { z } from 'zod'

import CreateUserTemplate from '~/components/band/signup/init/createUser/template.vue'
import VTextInput from '~/components/ui/Inputs/VText.vue'

import { useSegmentIdentify } from '~/composables/Segment/useSegmentIdentify'
import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useCustomerIO } from '~/composables/useCustomerIO'
import { useFieldValidation } from '~/composables/useFieldValidation'
import { getLocaleFromLanguage, useLanguage } from '~/composables/useLanguage'
import { useProvideAuthFetch } from '~/composables/useProvideAuthFetch'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserStore } from '~/stores/user'

import { setGrowthBookAttributes } from '~/helpers/GrowthBookHelpers'
import { getLPVariantFromLocalStorage } from '~/helpers/LandingPageABTest'
import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { provideAuthApiRegister, registerCallFailed } from '~/api-auth/Register'
import {
  provideLoginSession,
  provideLogoutSession,
} from '~/api-core/Login/Session'

import type { AuthUserReturn } from '~/api-core/Account/AuthUser'
import type { ProfileType } from '~/types/Auth'

type Props = {
  influencerEntity?: string
  referrerAgencyId?: number
}
type Emits = {
  'proceed-with-login': [profileType: ProfileType]
  'signup-success': [user: AuthUserReturn]
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const { $growthBook, $sentry } = useNuxtApp()
const { t } = useI18n()
const route = useRoute()
const { coreFetch } = useProvideCoreFetch()
const { authFetch } = useProvideAuthFetch()
const segmentIdentify = useSegmentIdentify()
const segmentTrack = useSegmentTrack()
const { customerIOIdentify } = useCustomerIO()
const { getCurrentLanguage } = useLanguage()
const { path: routePath, query: routeQuery } = route

const loading = ref(false)
const email = ref('')
const firstName = ref('')
const authServiceError = ref<null | string[]>(null)
const displayPasswordAnnotation = ref(false)
const displayPassword = ref(false)

const {
  value: passwordFieldValue,
  errorMessage: passwordFieldErrorMessage,
  validate: validatePasswordField,
  resetField: resetPasswordField,
} = useFieldValidation(
  'password',
  z.string().min(8, t('auth.login.error.passwordMinLength')),
  {
    syncVModel: true,
    initialValue: '',
  },
)

const { IS_INF: USER_IS_CURATOR } = useUserStore()
const inputType = computed<'text' | 'password'>(() =>
  displayPassword.value ? 'text' : 'password',
)

const isPasswordValid = computed(() => {
  return !!(!passwordFieldErrorMessage.value && passwordFieldValue.value.length)
})

onMounted(() => resetPasswordField())

async function trackFinalizeEvent(userId: number, userEmail: string) {
  trackEvent(
    {
      category: 'Band Signup',
      action: 'Create User',
      label: 'mail',
      isReferral: routePath.includes('referral'),
    },
    route,
  )

  const userTrackingData = {
    sign_up_method: 'email' as const,
    has_come_from_referral: routePath.includes('referral'),
    has_come_from_widget: routeQuery.utm_source === 'widget',
  }

  customerIOIdentify(userId, {
    email: userEmail,
  })

  // identify with segment
  await segmentIdentify({
    ...userTrackingData,
    user_id: userId,
    date_sign_up: new Date(),
  })

  segmentTrack('Sign Up - Sign Up Finalized', {
    ...userTrackingData,
    has_come_from_curator_dash: USER_IS_CURATOR,
    referrer_agency_id: props.referrerAgencyId || undefined,
    widget_curator_name: props.influencerEntity || undefined,
    landing_page_url: routePath,
    landing_page_accessed_from: window.localStorage.getItem(
      'comesFromCustomSignup',
    )
      ? 'CDBaby'
      : 'Groover',
    landing_page_variant: getLPVariantFromLocalStorage(),
  })
}

async function authV2Signup() {
  const registerUserOnAuth = provideAuthApiRegister(authFetch)
  const logUserToCore = provideLoginSession(coreFetch)

  try {
    const registerResponse = await registerUserOnAuth({
      accepted_tos: true,
      first_name: firstName.value,
      lang: getLocaleFromLanguage(getCurrentLanguage()),
      email: email.value.toLowerCase().trim(),
      password1: passwordFieldValue.value,
      password2: passwordFieldValue.value,
    })

    await provideLogoutSession(coreFetch)()
    const user = await logUserToCore(registerResponse.access)

    return user
  } catch (error) {
    if (!authFetch.$isFetchError(error)) throw error

    const data = error?.data
    if (registerCallFailed(data)) {
      authServiceError.value = data
        ? Object.entries(data).reduce<string[]>((acc, [, errMessages]) => {
            if (errMessages) acc.push(...errMessages)

            return acc
          }, [])
        : null
    }

    throw new Error('Failed register')
  }
}

async function finalizeSignup() {
  if (loading.value) return

  authServiceError.value = null
  try {
    const { valid } = await validatePasswordField()
    if (!valid) return

    loading.value = true

    const newCoreUser = await authV2Signup()

    trackFinalizeEvent(newCoreUser.id, newCoreUser.email)
    setGrowthBookAttributes({ userId: newCoreUser.id }, $growthBook)
    emit('signup-success', newCoreUser)
  } catch (err) {
    Sentry.captureException(err)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="tw-relative">
    <CreateUserTemplate
      :first-name="firstName"
      :email="email"
      :disabled="loading"
      @update-email="email = $event"
      @update-first-name="firstName = $event"
      @request-signup="finalizeSignup"
      @request-validation="validatePasswordField()"
    >
      <template #mid>
        <div class="tw-relative">
          <VTextInput
            v-model="passwordFieldValue"
            :p-validity="!passwordFieldErrorMessage"
            left-picto="fa-solid:lock"
            hide-right-picto
            :label="$t('band.signup.init.classicUser.input.passwordLabel')"
            :placeholder="
              $t('band.signup.init.classicUser.input.passwordPlaceholder')
            "
            :type="inputType"
            :display-right-check="false"
            :offset="80"
            data-test-id="emailArtistSignupPasswordInput"
            @focus="displayPasswordAnnotation = true"
            @blur="displayPasswordAnnotation = false"
          />
          <transition name="scale" mode="out-in">
            <button
              :key="displayPassword.toString()"
              type="button"
              class="tw-absolute tw-right-lg tw-top-[42px] tw-cursor-pointer tw-text-base tw-text-discrete2"
              tabindex="-1"
              @click.stop="displayPassword = !displayPassword"
            >
              <UIIcon
                :name="displayPassword ? 'fa-solid:eye-slash' : 'fa-solid:eye'"
                class="tw-text-icon-discrete2"
              />
            </button>
          </transition>
        </div>
        <transition name="fade" mode="out-in">
          <div
            v-if="passwordFieldErrorMessage"
            key="error"
            class="tw-mt-2 tw-flex tw-items-center tw-gap-x-2xs"
          >
            <UIIcon
              v-show="passwordFieldValue.length > 0"
              name="fa-solid:times"
              class="tw-text-icon-error"
              size="sm"
            />
            <span
              v-if="!passwordFieldValue.length"
              class="tw-text-form-error"
              >{{ $t('errors.input.required_field') }}</span
            >
            <span
              v-else
              class="tw-text-body-sm tw-font-medium tw-text-discrete2"
              >{{ $t('errors.input.min_characters', 8) }}</span
            >
          </div>
          <div
            v-else-if="displayPasswordAnnotation"
            key="focus"
            class="tw-mt-sm tw-flex tw-items-center tw-gap-x-2xs"
          >
            <UIIcon
              :name="isPasswordValid ? 'fa6-solid:check' : 'fa-solid:times'"
              :class="[
                isPasswordValid
                  ? 'tw-text-icon-success'
                  : 'tw-text-icon-discrete2',
              ]"
              size="sm"
            />
            <span
              class="tw-text-body-sm tw-font-medium"
              :class="[
                isPasswordValid ? 'tw-text-success' : 'tw-text-discrete2',
              ]"
              >{{ $t('errors.input.min_characters', 8) }}</span
            >
          </div>
        </transition>
        <transition name="fade" mode="out-in">
          <div
            v-if="authServiceError"
            class="tw-mx-auto tw-pt-4 tw-text-center"
          >
            <span
              v-for="errorMessage in authServiceError"
              :key="errorMessage"
              class="tw-text-form-error tw-inline-block"
            >
              {{ errorMessage }}
            </span>
          </div>
        </transition>
      </template>
    </CreateUserTemplate>
  </div>
</template>
